import type { AuditLog } from "@/hooks/useAdminAuditLogs";

type ChangeValue = { from: unknown; to: unknown } | unknown;

const FIELD_NAMES: Record<string, string> = {
  credits_balance: "Credits",
  is_unlimited: "Unlimited",
  unlimited_expires_at: "Unlimited expires",
  is_admin: "Admin",
  first_name: "First name",
  last_name: "Last name",
  phone: "Phone",
  member_type: "Member type",
};

function formatFieldName(field: string): string {
  return FIELD_NAMES[field] || field.replace(/_/g, " ");
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (typeof value === "string" && value.match(/^\d{4}-\d{2}-\d{2}/)) {
    // Date string - format nicely
    try {
      return new Date(value).toLocaleDateString();
    } catch {
      return value;
    }
  }
  return String(value);
}

function isFromToFormat(value: unknown): value is { from: unknown; to: unknown } {
  return (
    typeof value === "object" &&
    value !== null &&
    "from" in value &&
    "to" in value
  );
}

export function generateAuditSummary(log: AuditLog): string {
  if (log.action === "delete") {
    return "User permanently deleted";
  }

  if (log.action === "manage_tags") {
    const added = (log.details?.added as string[]) || [];
    const removed = (log.details?.removed as string[]) || [];
    const parts: string[] = [];
    if (added.length > 0) parts.push(`+${added.join(", ")}`);
    if (removed.length > 0) parts.push(`-${removed.join(", ")}`);
    return parts.length > 0 ? `Tags: ${parts.join("; ")}` : "Tags updated";
  }

  if (log.action === "bulk_manage_tags") {
    const userCount = (log.details?.user_count as number) || 0;
    const added = (log.details?.added as string[]) || [];
    const removed = (log.details?.removed as string[]) || [];
    const parts: string[] = [];
    if (added.length > 0) parts.push(`+${added.join(", ")}`);
    if (removed.length > 0) parts.push(`-${removed.join(", ")}`);
    const tagPart = parts.length > 0 ? parts.join("; ") : "updated";
    return `Bulk tags (${userCount} users): ${tagPart}`;
  }

  // Tag CRUD operations
  if (log.action === "create_tag") {
    const tagName = log.details?.tag_name as string;
    return `Created tag "${tagName}"`;
  }

  if (log.action === "update_tag") {
    const tagName = log.details?.tag_name as string;
    const changes = log.details?.changes as string[] | undefined;
    if (changes && changes.length > 0) {
      return `Updated tag "${tagName}" (${changes.join(", ")})`;
    }
    return `Updated tag "${tagName}"`;
  }

  if (log.action === "delete_tag") {
    const tagName = log.details?.tag_name as string;
    const userCount = log.details?.users_affected as number;
    if (userCount && userCount > 0) {
      return `Deleted tag "${tagName}" (removed from ${userCount} user${userCount !== 1 ? "s" : ""})`;
    }
    return `Deleted tag "${tagName}"`;
  }

  // Tag rule operations
  if (log.action === "create_tag_rule") {
    return "Created auto-tag rule";
  }

  if (log.action !== "update" || !log.details?.changes) {
    return log.action;
  }

  const changes = log.details.changes as Record<string, ChangeValue>;
  const summaryParts: string[] = [];

  for (const [field, value] of Object.entries(changes)) {
    // Handle new format: { from: X, to: Y }
    if (isFromToFormat(value)) {
      const { from, to } = value;
      const formattedFrom = formatValue(from);
      const formattedTo = formatValue(to);
      // Only show if there's an actual visible change
      if (formattedFrom !== formattedTo) {
        summaryParts.push(
          `${formatFieldName(field)} ${formattedFrom} → ${formattedTo}`
        );
      }
    } else {
      // Handle legacy format (just the new value)
      summaryParts.push(`${formatFieldName(field)}: ${formatValue(value)}`);
    }
  }

  if (summaryParts.length === 0) {
    return "No changes detected";
  }

  // Show first 2 changes, add ellipsis if more
  const display = summaryParts.slice(0, 2).join(", ");
  return summaryParts.length > 2 ? `${display}...` : display;
}
